// Rows' sea: wave phases, per-row parallax offsets and count's row window, stepped per frame by the runtime.
import { VOCAB } from '../vocab.js'

/** Rows holding a parallax offset: count's most and three past each edge (light.glsl.js `SEA_RING`). */
export const SEA_RING = VOCAB.count.maximum + 6

// Su per field cell: across the rows the field moves hill_rows cells a row (displacement.glsl.js `SEA_CELL`).
const SEA_CELL = 137.5
// The window's edges ease toward count's at this rate, per second.
const WIN_RATE = 4
// φ1 − φ2 − φ3 = −¼ per wave keeps the peaks round.
const START = [
	[0.37, 0.12, 0.5],
	[0, 0.31, 0.94],
	[0.62, 0.45, 0.42],
]

const wrap = (x, c) => x - Math.floor(x / c) * c

// Each wave's height, length and speed uniforms; B's and C's speeds are multiples of A's.
const WAVES = [
	['uWaveHeight', 'uWaveLength', null],
	['uWaveBHeight', 'uWaveBLength', 'uWaveBSpeed'],
	['uWaveCHeight', 'uWaveCLength', 'uWaveCSpeed'],
]

// A wave's trains at length su, waves per cell: train 1 the sum of train 2 (+60°) and train 3 (−60°).
const trains = length => {
	const m = SEA_CELL / Math.max(length, 1)
	return [
		[m, 0],
		[0.5 * m, 0.8660254 * m],
		[0.5 * m, -0.8660254 * m],
	]
}

// Each row's trio along it as sin 2θ·a + cos 2θ·b + sin θ·c + cos θ·d, θ = ½·m·x (displacement.glsl.js `rowCrestAlong`).
const crestRows = (sea, u, travel) => {
	const out = new Float32Array(SEA_RING * 12)
	const hill = u.uHillRows ?? 0
	for (const [id, o] of sea.rows) {
		const fy = (id - travel) * hill
		WAVES.forEach(([, length], w) => {
			const p = trains(u[length]).map(([, ky], j) => 2 * Math.PI * (ky * fy + sea.phases[w][j] + o[w * 3 + j]))
			out.set(
				[Math.cos(p[0]), Math.sin(p[0]), Math.cos(p[1]) + Math.cos(p[2]), Math.sin(p[1]) + Math.sin(p[2])],
				(wrap(id, SEA_RING) * 3 + w) * 4
			)
		})
	}
	return out
}

/** The uniforms the sea uploads (light.glsl.js, displacement.glsl.js), as Pixi declares them. */
export const seaUniformDecls = () => ({
	uSeaPhaseA: { value: new Float32Array(3), type: 'vec3<f32>' },
	uSeaPhaseB: { value: new Float32Array(3), type: 'vec3<f32>' },
	uSeaPhaseC: { value: new Float32Array(3), type: 'vec3<f32>' },
	uSeaRow: { value: new Float32Array(SEA_RING * 12), type: 'vec4<f32>', size: SEA_RING * 3 },
	uSeaCrest: { value: new Float32Array(SEA_RING * 12), type: 'vec4<f32>', size: SEA_RING * 3 },
	uRowWin: { value: new Float32Array([-0.5, 0.5]), type: 'vec2<f32>' },
	uRowHole: { value: new Float32Array(2), type: 'vec2<f32>' },
})

export const newSea = () => ({ phases: START.map(p => [...p]), rows: new Map(), win: null })

const rowsOf = count => Math.min(Math.max(count ?? 1, 1), VOCAB.count.maximum)

// Count's window, settled.
const windowOf = count => ({ front: -rowsOf(count) / 2, back: rowsOf(count) / 2, hole: null })

// Opening against the rows' travel, the front jumps and leaves a hole that rides with the rows until it leaves. A fractional count is a path's, already growing its next row in: the window is exactly count's.
const stepWindow = (sea, count, travel, moved, seconds) => {
	const n = rowsOf(count)
	const front = -n / 2
	if (n !== Math.round(n)) {
		sea.win = windowOf(count)
		return
	}
	const win = (sea.win ??= windowOf(count))
	const ease = (x, to) => x + (to - x) * (1 - Math.exp(-WIN_RATE * seconds))
	win.back = ease(win.back, n / 2)
	if (front > win.front) win.front = ease(win.front, front)
	else if (moved > 0) win.front = Math.max(front, win.front - moved)
	else if (moved < 0 && front < win.front) {
		const hole = [front + travel, win.front + travel]
		win.hole = win.hole ? [Math.min(win.hole[0], hole[0]), Math.max(win.hole[1], hole[1])] : hole
		win.front = front
	}
	if (win.hole && (win.hole[0] - travel > win.back || win.hole[1] - travel < win.front)) win.hole = null
}

/** Moves the sea one frame: `seconds` of wave travel at wave A's `rate` (su/s), the rows `moved` on (speed's travel, rows) to unfolded `travel`. */
export const stepSea = (sea, u, { seconds, rate, moved, travel }) => {
	stepWindow(sea, u.uCount, travel, moved, seconds)
	const a = ((u.uWaveDirection ?? 0) * Math.PI) / 180
	const d = [Math.cos(a), Math.sin(a)]
	const cam = moved * (u.uHillRows ?? 0)
	const tracks = []
	WAVES.forEach(([, length, speed], w) => {
		const su = rate * (speed ? (u[speed] ?? 0) : 1) * seconds
		trains(u[length]).forEach(([kx, ky], j) => {
			const track = ((kx * d[0] + ky * d[1]) * su) / SEA_CELL
			sea.phases[w][j] = wrap(sea.phases[w][j] + ky * cam - track, 1)
			tracks.push(track)
		})
	})
	const p = Math.max(u.uParallax ?? 1, 1)
	const lo = Math.floor(travel + sea.win.front) - 1
	const hi = Math.ceil(travel + sea.win.back) + 1
	for (const id of sea.rows.keys()) if (id < lo || id > hi) sea.rows.delete(id)
	for (let id = lo; id <= hi; id++) {
		const o = sea.rows.get(id) ?? new Array(9).fill(0)
		const excess = p ** -(id - travel) - 1
		for (let t = 0; t < 9; t++) o[t] = wrap(o[t] - excess * tracks[t], 1)
		sea.rows.set(id, o)
	}
}

/** Writes the sea's uniforms into `u` at unfolded `travel`, into the arrays already there (Pixi's) or new ones; a sea with no window takes count's. */
export const uploadSea = (sea, u, travel) => {
	sea.win ??= windowOf(u.uCount)
	const set = (name, values) => {
		if (!(u[name] instanceof Float32Array) || u[name].length !== values.length)
			u[name] = new Float32Array(values.length)
		u[name].set(values)
	}
	set('uSeaPhaseA', sea.phases[0])
	set('uSeaPhaseB', sea.phases[1])
	set('uSeaPhaseC', sea.phases[2])
	const rows = new Float32Array(SEA_RING * 12)
	for (const [id, o] of sea.rows)
		for (let w = 0; w < 3; w++) rows.set(o.slice(w * 3, w * 3 + 3), (wrap(id, SEA_RING) * 3 + w) * 4)
	set('uSeaRow', rows)
	set('uSeaCrest', crestRows(sea, u, travel))
	const { win } = sea
	set('uRowWin', [win.front, win.back])
	set('uRowHole', win.hole ? win.hole.map(x => x - travel) : [0, 0])
}

/** The sea after `seconds` at constant values from a still start, the rows `travel` on: count's window settled, every row in it there all along. Into `u` (numbers in arrays). */
export const seaAt = (u, { seconds, rate, travel }) => {
	const sea = newSea()
	sea.win = windowOf(u.uCount)
	stepSea(sea, u, { seconds, rate, moved: travel, travel })
	const set = (name, values) => (u[name] = [...values])
	set('uSeaPhaseA', sea.phases[0])
	set('uSeaPhaseB', sea.phases[1])
	set('uSeaPhaseC', sea.phases[2])
	const rows = new Array(SEA_RING * 12).fill(0)
	for (const [id, o] of sea.rows)
		for (let w = 0; w < 3; w++)
			o.slice(w * 3, w * 3 + 3).forEach((v, j) => (rows[(wrap(id, SEA_RING) * 3 + w) * 4 + j] = v))
	u.uSeaRow = rows
	u.uSeaCrest = [...crestRows(sea, u, travel)]
	u.uRowWin = [sea.win.front, sea.win.back]
	u.uRowHole = [0, 0]
	return u
}
